import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, IconButton, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem, AppBar, Toolbar,
  InputAdornment, CircularProgress, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Snackbar, Alert
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon
} from '@mui/icons-material';
import api from '../api/api';

const SubZonas = () => {
  const [subZonas, setSubZonas] = useState([]);
  const [originalSubZonas, setOriginalSubZonas] = useState([]);
  const [zonas, setZonas] = useState([]); // Para el select del formulario
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubZona, setSelectedSubZona] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_zona: '',
    nombre_sub_zona: '',
    activo: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  useEffect(() => {
    fetchData();
  }, []);

  // Crea un diccionario { id_zona: nombre_zona } para búsquedas instantáneas
const zonasMap = React.useMemo(() => {
  const map = {};
  zonas.forEach(z => {
    map[z.id_zona] = z.nombre_zona;
  });
  return map;
}, [zonas]);

  // Filtrado en tiempo real
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSubZonas(originalSubZonas);
    } else {
      const filtered = originalSubZonas.filter((sz) =>
        sz.nombre_sub_zona.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSubZonas(filtered);
    }
  }, [searchTerm, originalSubZonas]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resSub, resZonas] = await Promise.all([
        api.get('/sub_zonas/'),
        api.get('/zonas/')
      ]);
      setSubZonas(resSub.data);
      setOriginalSubZonas(resSub.data);
      setZonas(resZonas.data);
    } catch (err) {
      showSnackbar('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (subZona = null) => {
    if (subZona) {
      setSelectedSubZona(subZona);
      setFormData({
        id_zona: subZona.id_zona,
        nombre_sub_zona: subZona.nombre_sub_zona,
        activo: subZona.activo,
      });
    } else {
      setSelectedSubZona(null);
      setFormData({ id_zona: '', nombre_sub_zona: '', activo: true });
    }
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      if (selectedSubZona) {
        await api.put(`/sub_zonas/${selectedSubZona.id_sub_zona}`, formData);
        showSnackbar('Subzona actualizada correctamente');
      } else {
        await api.post('/sub_zonas/', formData);
        showSnackbar('Subzona creada correctamente');
      }
      setOpenModal(false);
      fetchData();
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Error al guardar', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta subzona?')) {
      try {
        await api.delete(`/sub_zonas/${id}`);
        showSnackbar('Subzona eliminada');
        fetchData();
      } catch (err) {
        showSnackbar('Error al eliminar', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f6f8' }}>
      <AppBar position="static" sx={{ bgcolor: 'white', color: 'text.primary', boxShadow: 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mr: 3 }}>
              Gestión de Sub-Zonas
            </Typography>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
              Nueva Sub-Zona
            </Button>
          </Box>
          <TextField
            size="small"
            placeholder="Buscar sub-zona..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sub-Zona</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Zona Padre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subZonas.map((sz) => (
                  <TableRow key={sz.id_sub_zona} hover>
                    <TableCell>{sz.nombre_sub_zona}</TableCell>
                    <TableCell>
                      {zonasMap[sz.id_zona] || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', px: 1, py: 0.5, borderRadius: 1,
                        bgcolor: sz.activo ? '#e8f5e9' : '#ffebee',
                        color: sz.activo ? '#2e7d32' : '#c62828',
                        fontWeight: 'bold', fontSize: '0.75rem'
                      }}>
                        {sz.activo ? 'ACTIVA' : 'INACTIVA'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton color="primary" onClick={() => handleOpenModal(sz)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(sz.id_sub_zona)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
            {selectedSubZona ? 'Editar Sub-Zona' : 'Nueva Sub-Zona'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Seleccionar Zona Padre"
              value={formData.id_zona}
              onChange={(e) => setFormData({ ...formData, id_zona: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
            >
              {zonas.map((z) => (
                <MenuItem key={z.id_zona} value={z.id_zona}>{z.nombre_zona}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Nombre de la Sub-Zona"
              value={formData.nombre_sub_zona}
              onChange={(e) => setFormData({ ...formData, nombre_sub_zona: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              />
              <Typography>Activa</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} variant="contained" disabled={!formData.nombre_sub_zona || !formData.id_zona}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SubZonas;